import { Box, Button, Card, CardContent, Menu, MenuItem, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import BarChart from "../../../components/BarChart";
import { ApiContext } from "../../../App";
import React from "react";
import { useTranslation } from "react-i18next";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { get } from "underscore";



export const savingsTypeEnum = {
    electricity: "kwh",
    cost: "cost",
    co2: "co2"
}


const chartTypeToSavingsTypeMap = {
    "consumption_kwh": savingsTypeEnum.electricity,
    "cost": savingsTypeEnum.cost,
    "co2_kg": savingsTypeEnum.co2
}

const getBucketTimestamp = (dateStr, timeAggType) => {
    const dateObj = new Date(dateStr);
    const bucketDate = new Date(dateObj);

    if (timeAggType === "d") {
        bucketDate.setHours(0, 0, 0, 0);
    } else if (timeAggType === "m") {
        bucketDate.setDate(1);
        bucketDate.setHours(0, 0, 0, 0);
    } else if (timeAggType === "q") {
        const month = dateObj.getMonth();
        const quarterMonth = Math.floor(month / 3) * 3;
        bucketDate.setMonth(quarterMonth);
        bucketDate.setDate(1);
        bucketDate.setHours(0, 0, 0, 0);
    } else if (timeAggType === "y") {
        bucketDate.setMonth(0);
        bucketDate.setDate(1);
        bucketDate.setHours(0, 0, 0, 0);
    }

    return bucketDate.toISOString();
};


const getSeriesName = (chartType, savingsMode, t) => {
    const unit = savingsMode === "percentage"
        ? "%"
        : chartType === "cost"
            ? "$"
            : chartType === "consumption_kwh"
                ? "kWh"
                : "kgCO2";

    const seriesName = savingsMode === "percentage"
        ? `${t("Savings")} (%)`
        : `${t("Savings")} (${unit})`;
    return seriesName;
}

const getSavingsValue = (actual, baseline, savingsMode) => {
    let value = null;
    if (savingsMode === "percentage") {
        if (baseline === 0) {
            return value = 0;
        }
        value = ((baseline - actual) / baseline) * 100;
    } else {
        value = baseline - actual;
    }
    return value;
}

const buildBarChartSeries = ({ dates, baselines, actuals }, savingsMode, chartType, t) => {
    const seriesName = getSeriesName(chartType, savingsMode, t);
    let chartValues = [];
    let datesWithData = [];
    dates.forEach((date, index) => {
        const baseline = baselines[index];
        const actual = actuals[index];
        const savingsValue = getSavingsValue(actual, baseline, savingsMode);
        chartValues.push(savingsValue);
        datesWithData.push(date);
    });


    return {
        timestamps: datesWithData,
        data: [{
            metadata: { name: seriesName },
            values: chartValues
        }]
    };
};

const aggregatePerformanceReportPerDate = (apiData, chartType, timeAggType, savingsType, savingsMode) => {
    const dates = apiData?.dates ?? [];
    const actuals = apiData?.daily_performance_breakdowns?.actual[savingsType] ?? [];
    const baselines = apiData?.daily_performance_breakdowns?.baseline[savingsType] ?? [];

    if (!dates.length || !actuals.length || !baselines.length || dates.length !== actuals.length || dates.length !== baselines.length) {
        if ((dates.length !== actuals.length || dates.length !== baselines.length) && dates.length && (actuals.length || baselines.length)) {
            console.error("Mismatched data lengths", apiData);
        }
        return { dates: [], baselines: [], actuals: [] };
    }

    let bucketDates = [];
    let bucketedActualValues = [];
    let bucketedBaselineValues = [];

    dates.forEach((dateStr, index) => {
        const actual = actuals[index];
        const baseline = baselines[index];
        const bucketTimestamp = getBucketTimestamp(dateStr, timeAggType);

        const existingBucketIndex = bucketDates.indexOf(bucketTimestamp);
        if (existingBucketIndex === -1) {
            bucketDates.push(bucketTimestamp);
            bucketedActualValues.push(actual);
            bucketedBaselineValues.push(baseline);
        } else {
            bucketedActualValues[existingBucketIndex] += actual;
            bucketedBaselineValues[existingBucketIndex] += baseline;
        }
    });

    return {
        dates: bucketDates,
        baselines: bucketedBaselineValues,
        actuals: bucketedActualValues,
    }
};


export default function PortfolioSavingsChart(props) {

    const { startDate, endDate, localTimeZone, currencySymbol } = props;

    const [apiData, setApiData] = React.useState([]);// { dates: [], daily_performance_breakdowns: { baseline: { cost: [], consumption_kwh: [], co2_kg: [] }, actual: { cost: [], consumption_kwh: [], co2_kg: [] } }[]   }
    const [timeAggType, setTimeAggType] = React.useState("d"); //m,q,y
    const [chartType, setChartType] = React.useState("cost"); //consumption_kwh, cost, co2
    const [savingsMode, setSavingsMode] = React.useState("absolute"); // "absolute" or "percentage"
    const [anchorEl, setAnchorEl] = React.useState(false);
    const menuOpen = Boolean(anchorEl);


    const { hvacApi } = React.useContext(ApiContext);
    const { t, i18n } = useTranslation();

    const onSuccess = (data) => {
        setApiData(data);
    }

    const onError = (data) => {
        setApiData([]);

    }

    const fetchChartData = (startDate, endDate) => {
        hvacApi.getPortfolioSavingsReport(
            startDate,
            endDate,
            onSuccess,
            onError
        )
    }

    React.useEffect(() => {
        fetchChartData(startDate, endDate);
    }, [startDate, endDate]);

    const timeAggregatedData = React.useMemo(() => {
        return aggregatePerformanceReportPerDate(
            apiData,
            chartType,
            timeAggType,
            chartTypeToSavingsTypeMap[chartType],
            savingsMode
        );
    }, [apiData, timeAggType, chartType, savingsMode]);

    const transformedChartData = React.useMemo(() => {
        return buildBarChartSeries(timeAggregatedData, savingsMode, chartType, t);
    }, [timeAggregatedData, savingsMode, chartType, t]);

    const absoluteSavingsSymbol = chartType === "cost" ? currencySymbol : chartType === "consumption_kwh" ? "kWh" : "kg";


    const handleTimeAggChange = (event, newValue) => {
        setTimeAggType(newValue);
    }

    const handleSavingsModeChange = (event, newValue) => {
        if (newValue) {
            setSavingsMode(newValue);
        }
    }


    return (
        <Card>
            <Stack direction={"row"} spacing={2}>
                <Button
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={{ textTransform: 'none' }}
                    variant="text"
                    disableElevation
                    endIcon={<ArrowDropDownIcon />}
                >
                    <Typography variant="h6">
                        {(chartType === "cost") && t("Cost")}
                        {(chartType === "consumption_kwh") && t("Consumption")}
                        {(chartType === "co2_kg") && t("CO2")}
                    </Typography>
                </Button>
                <Menu open={menuOpen} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
                    <MenuItem onClick={() => { setChartType("cost"); setAnchorEl(null); }}>{t("Cost")}</MenuItem>
                    <MenuItem onClick={() => { setChartType("consumption_kwh"); setAnchorEl(null); }}>{t("Consumption")}</MenuItem>
                    <MenuItem onClick={() => { setChartType("co2_kg"); setAnchorEl(null); }}>{t("CO2")}</MenuItem>
                </Menu>


            </Stack>
            <Stack direction={"row"} spacing={2} padding={2}>
                <ToggleButtonGroup
                    value={savingsMode}
                    exclusive
                    size="small"
                    onChange={handleSavingsModeChange}
                >
                    <ToggleButton value="absolute">{absoluteSavingsSymbol}</ToggleButton>
                    <ToggleButton value="percentage">%</ToggleButton>
                </ToggleButtonGroup>
                <Box flexGrow={1}></Box>
                <ToggleButtonGroup
                    value={timeAggType}
                    exclusive
                    size="small"
                    onChange={handleTimeAggChange}
                >
                    <ToggleButton value="d">{t("D")}</ToggleButton>
                    <ToggleButton value="m">{t("M")}</ToggleButton>
                    <ToggleButton value="q">{t("Q")}</ToggleButton>
                    <ToggleButton value="y">{t("Y")}</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            <CardContent>
                <BarChart data={transformedChartData.data} timeAggType={timeAggType} timestamps={transformedChartData.timestamps} localTimeZone={localTimeZone} />
            </CardContent>
        </Card>
    );
}